import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const PortalContext = createContext(null);

export const usePortal = () => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
};

export const PortalProvider = ({ children }) => {
  console.log('PortalProvider initializing');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in
  const checkSession = useCallback(async () => {
    try {
      // Add cache-busting to prevent stale session data
      const timestamp = Date.now();
      console.log('[PortalContext] Checking session at:', `${BASE_URL}/api/portal/session`);
      const response = await fetch(`${BASE_URL}/api/portal/session?_=${timestamp}`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const data = await response.json();
      console.log('[PortalContext] Session check response:', data);

      if (data.authenticated) {
        // Get customer info
        const customerRes = await fetch(`${BASE_URL}/api/portal/me`, {
          credentials: 'include'
        });
        if (customerRes.ok) {
          const customerData = await customerRes.json();
          setCustomer(customerData);
          console.log('[PortalContext] Customer loaded:', customerData.email);
        } else if (customerRes.status === 404 || customerRes.status === 500) {
          // Customer not found in DB (404) or server error (500) - create customer object from session data
          // Use name and address from session - do NOT fall back to email prefix
          const fallbackCustomer = {
            email: data.email,
            name: data.name || data.email,
            address: data.address || ''
          };
          console.log('[PortalContext] Customer lookup failed (status:', customerRes.status, '), using session data:', fallbackCustomer);
          setCustomer(fallbackCustomer);
        } else {
          console.error('[PortalContext] Failed to load customer, status:', customerRes.status);
        }
      } else {
        console.log('[PortalContext] Not authenticated');
      }
    } catch (err) {
      console.error('[PortalContext] Session check error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run session check on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const sendMagicLink = useCallback(async (email) => {
    try {
      setError(null);
      const response = await fetch(`${BASE_URL}/api/portal/send-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send login link');
      }

      return { success: true, message: data.message };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Clear customer state first
      setCustomer(null);
      // Call server to destroy session
      await fetch(`${BASE_URL}/api/portal/logout`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store'
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Force a full page reload to clear all cached data
      window.location.replace('/portal/login');
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      // Add cache-busting parameter and headers
      const timestamp = Date.now();
      const response = await fetch(`${BASE_URL}/api/portal/invoices?_=${timestamp}`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setCustomer(null);
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch invoices');
      }

      return await response.json();
    } catch (err) {
      console.error('Fetch invoices error:', err);
      throw err;
    }
  }, []);

  const fetchInvoice = useCallback(async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/api/portal/invoices/${id}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          setCustomer(null);
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch invoice');
      }

      return await response.json();
    } catch (err) {
      console.error('Fetch invoice error:', err);
      throw err;
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      // Add cache-busting parameter and headers
      const timestamp = Date.now();
      const response = await fetch(`${BASE_URL}/api/portal/jobs?_=${timestamp}`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setCustomer(null);
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch jobs');
      }

      return await response.json();
    } catch (err) {
      console.error('Fetch jobs error:', err);
      throw err;
    }
  }, []);

  /** All business jobs in [from, to] (YYYY-MM-DD) for portal week grid — includes others' jobs as busy blocks. */
  const fetchBusinessSchedule = useCallback(async (from, to) => {
    try {
      const timestamp = Date.now();
      const q = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&_=${timestamp}`;
      const response = await fetch(`${BASE_URL}/api/portal/business-schedule?${q}`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (!response.ok) {
        if (response.status === 401) {
          setCustomer(null);
          throw new Error('Session expired. Please login again.');
        }
        if (response.status === 404) {
          return { jobs: [] };
        }
        throw new Error('Failed to fetch schedule');
      }
      return await response.json();
    } catch (err) {
      if (err && err.message && err.message.includes('Session expired')) throw err;
      console.error('Fetch business schedule error:', err);
      return { jobs: [] };
    }
  }, []);

  const fetchServiceRequests = useCallback(async () => {
    try {
      const timestamp = Date.now();
      const response = await fetch(`${BASE_URL}/api/portal/service-requests?_=${timestamp}`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (!response.ok) {
        if (response.status === 401) {
          setCustomer(null);
          throw new Error('Session expired. Please login again.');
        }
        if (response.status === 404) {
          console.warn(
            '[Portal] GET /api/portal/service-requests returned 404. Start the backend (cd backend && node server.js).'
          );
          return [];
        }
        throw new Error('Failed to fetch service requests');
      }
      return await response.json();
    } catch (err) {
      if (err && err.message && err.message.includes('Session expired')) throw err;
      console.error('Fetch service requests error:', err);
      return [];
    }
  }, []);

  const submitServiceRequest = useCallback(async ({ issue, preferred_date, preferred_time }) => {
    const response = await fetch(`${BASE_URL}/api/portal/service-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ issue, preferred_date, preferred_time: preferred_time || null })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(
          'Could not reach the server API (404). From the project folder run: cd backend && node server.js — then restart npm start in frontend so /api is proxied to port 3001.'
        );
      }
      throw new Error(data.error || 'Failed to submit request');
    }
    return data;
  }, []);

  /** Reschedule a portal service request (still in review). */
  const updateServiceRequest = useCallback(async (id, { preferred_date, preferred_time }) => {
    const response = await fetch(`${BASE_URL}/api/portal/service-request/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ preferred_date, preferred_time: preferred_time || null })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 401) {
        setCustomer(null);
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(data.error || 'Failed to update request');
    }
    return data;
  }, []);

  const createPaymentIntent = useCallback(async (invoiceId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/portal/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ invoiceId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create payment');
      }

      return await response.json();
    } catch (err) {
      console.error('Create payment intent error:', err);
      throw err;
    }
  }, []);

  const confirmPayment = useCallback(async (invoiceId, paymentIntentId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/portal/confirm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ invoiceId, paymentIntentId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to confirm payment');
      }

      return await response.json();
    } catch (err) {
      console.error('Confirm payment error:', err);
      throw err;
    }
  }, []);

  const value = useMemo(() => ({
    customer,
    loading,
    error,
    isAuthenticated: !!customer,
    sendMagicLink,
    logout,
    fetchInvoices,
    fetchInvoice,
    fetchJobs,
    fetchBusinessSchedule,
    fetchServiceRequests,
    submitServiceRequest,
    updateServiceRequest,
    createPaymentIntent,
    confirmPayment,
    refreshSession: checkSession
  }), [customer, loading, error, sendMagicLink, logout, fetchInvoices, fetchInvoice, fetchJobs, fetchBusinessSchedule, fetchServiceRequests, submitServiceRequest, updateServiceRequest, createPaymentIntent, confirmPayment, checkSession]);

  console.log('PortalProvider rendering with customer:', customer ? 'yes' : 'no');

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
};

// Error boundary wrapper for Portal
export class PortalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Portal error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding: '40px', background: 'white', color: 'red'}}>
          <h1>Portal Error</h1>
          <p>{this.state.error?.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}
